/**
 * Pitch auto-calibration orchestrator (REFSIX-669 / "PitchNet", research exp24).
 *
 * Given a match's raw GPS track, predict the pitch calibration points so the
 * heatmap / sprintmap / cardmap render without the manual 3-tap flow. The chain
 * (per the research §11.1 reference) is:
 *
 *   raw GPS -> preprocessGps -> guard -> computePca -> preparePointCloud
 *           -> [ PointNet forward pass ] -> destandardise -> boxParamsToCalibration
 *
 * The model runs as a pure-TS forward pass (pitchModelForward.ts) — no ONNX / ML
 * runtime, no WASM. Architecture decision (do not re-litigate): the model is the
 * ONLY mechanism — no pitch catalogue, no geometric fallback, no confidence
 * gate. If the track fails the sanity guard, this returns null and the caller
 * falls back to manual calibration.
 */

import {
    GpsCalibrationPoints,
    GpsPoint,
    PitchCalibrationMethod,
} from "refsix-js-models";
import { preprocessGps } from "./gpsPreprocessing";
import { forwardPitchNet, getPitchNetModel } from "./pitchModelForward";
import {
    boxParamsToCalibration,
    computePca,
    destandardise,
    preparePointCloud,
} from "./pitchGeometry";

export interface AutoCalibrateOptions {
    /**
     * Minimum cleaned points required to trust a prediction (sanity guard, not a
     * confidence gate). Defaults to 100 — below this the track is too sparse to
     * box reliably and we fall back to manual calibration.
     */
    minCleanedPoints?: number;
}

const DEFAULT_MIN_CLEANED_POINTS = 100;

/**
 * Predict calibration points for a GPS track, or null if the track is not
 * viable (caller should fall back to the manual calibration flow).
 */
export async function autoCalibrate(
    points: (GpsPoint | null | undefined)[],
    opts: AutoCalibrateOptions = {}
): Promise<GpsCalibrationPoints | null> {
    const minCleaned = opts.minCleanedPoints ?? DEFAULT_MIN_CLEANED_POINTS;

    // Clean with the reference defaults (min 50 internally), then apply the
    // separate viability guard on the final cleaned count.
    const cleaned = preprocessGps(points);
    if (!cleaned || cleaned.cleanedCount < minCleaned) {
        return null;
    }

    const pca = computePca(cleaned.lats, cleaned.lngs);
    const cloud = preparePointCloud(cleaned.lats, cleaned.lngs, pca);
    if (!cloud) {
        return null;
    }

    const model = getPitchNetModel();
    const raw = forwardPitchNet(cloud, model.weights);
    const out = destandardise(raw, model.mean4, model.std4);
    const cal = boxParamsToCalibration(out, pca);

    return {
        leftCorner: { latitude: cal.left[0], longitude: cal.left[1] },
        rightCorner: { latitude: cal.right[0], longitude: cal.right[1] },
        midfield: { latitude: cal.mid[0], longitude: cal.mid[1] },
        calibratedBy: PitchCalibrationMethod.PointNet,
    };
}
